import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { FIELD_KEYS } from '_constants/forms';
import { theme } from '_constants/theme';
import { getFieldProps } from '_helpers/forms/fields';

import Button from 'components/atomic/Button';
import Spinner from 'components/atomic/Spinner';
import BaseField from 'components/atomic/fields/BaseField';
import Text from 'components/atomic/layouts/Text';

const StyledRow = styled(Row)`
  padding-top: ${theme.spaces.regular}px;
`;

const StyledLinkContainer = styled.div`
  a {
    font-family: 'Inter';
    font-size: ${theme.fontSizes.small}px;
    font-weight: 400;
    line-height: 12px;
    color: ${theme.colors.gray.secondary};

    &:hover {
      color: ${theme.colors.indigo.primary};
    }
  }
`;

const StyledMessageContainer = styled.div`
  display: inline-flex;
  justify-content: flex-start;
  align-items: center;
  gap: ${theme.spaces.regular}px;
`;

const ForgotPasswordForm = ({ loading, success, error, submitDisabled, defaultValues, onSubmit }) => {
  const {
    control,
    formState: { isSubmitted, isSubmitSuccessful, submitCount },
    handleSubmit,
  } = useForm({
    defaultValues,
    reValidateMode: 'onChange',
  });

  const [emailVerifying, setEmailVerifying] = useState(false);

  useEffect(() => {
    if (isSubmitted && !isSubmitSuccessful && submitCount) {
      onSubmit();
    }
  }, [isSubmitted, isSubmitSuccessful, submitCount, onSubmit]);

  return (
    <Form noValidate onSubmit={handleSubmit(onSubmit)}>
      <Row>
        <Col xs={12}>
          <Text size={theme.fontSizes.regular} weight={400} color={theme.colors.gray.secondary}>
            Forgot your password?
          </Text>
          <Text
            size={theme.fontSizes.smallX}
            weight={400}
            color={theme.colors.gray.secondary}
            bottomSpace={theme.spaces.medium}
          >
            Enter your e-mail below for us to send you a reset code
          </Text>
        </Col>
        <Col xs={12}>
          <BaseField
            {...getFieldProps(FIELD_KEYS.email, {
              validationOptions: { setProcessing: setEmailVerifying },
            })}
            control={control}
            loading={emailVerifying}
            feedback={emailVerifying ? 'Verifying...' : undefined}
            bottomSpace={theme.spaces.regular}
          />
        </Col>
      </Row>
      <StyledRow>
        <Col xs={8}>
          <StyledLinkContainer>
            <Link to="../login">Return to Log In</Link>
          </StyledLinkContainer>
        </Col>
        <Col xs={4}>
          <Button type="submit" disabled={submitDisabled} color={theme.colors.indigo.primary}>
            Reset
          </Button>
        </Col>
      </StyledRow>

      {loading && (
        <Text size={theme.fontSizes.small} color={theme.colors.gray.secondary} topSpace={theme.spaces.regular}>
          <StyledMessageContainer>
            <Spinner />
            <div>Processing...</div>
          </StyledMessageContainer>
        </Text>
      )}

      {success && (
        <Text size={theme.fontSizes.small} color={theme.colors.green.primary} topSpace={theme.spaces.regular}>
          Thank you, please check your email for a password reset code
        </Text>
      )}

      {error && (
        <Text size={theme.fontSizes.small} color={theme.colors.red.primary} topSpace={theme.spaces.regular}>
          {error?.message}
        </Text>
      )}
    </Form>
  );
};

ForgotPasswordForm.propTypes = {
  loading: PropTypes.bool,
  success: PropTypes.bool,
  error: PropTypes.object,
  submitDisabled: PropTypes.bool,
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func,
};

export default ForgotPasswordForm;
